'use client';

import * as React from 'react';
import { useEffect, useCallback, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import {
  Clock,
  Coins,
  ShoppingCart,
  CheckCircle,
  WarningCircle,
  MapTrifold,
  CaretDown,
  X,
  NavigationArrow,
  Crosshair,
  ArrowClockwise,
  Phone,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/primitives/button';
import { useAddressStore, selectSelectedAddress } from '@/stores/address-store';
import { AddressInput } from './address-input';
import { deliveryApi } from '@/lib/api/delivery';
import type { YandexMapHandle } from './yandex-map';
import type { City } from '@/lib/api/delivery';
import { ymAddressSelected, tmrAddressSelected } from '@/lib/analytics';

// Dynamic import for Yandex Map
const YandexMap = dynamic(
  () => import('./yandex-map').then((mod) => mod.YandexMap),
  {
    ssr: false,
    loading: () => (
      <div className="w-full h-full bg-[var(--surface-secondary)] flex items-center justify-center">
        <MapTrifold className="h-10 w-10 text-[var(--text-tertiary)]" weight="thin" />
      </div>
    ),
  }
);

/* ============================
   City Selector
   ============================ */
function CitySelector({
  cities,
  selectedCity,
  onSelect,
}: {
  cities: City[];
  selectedCity: City | null;
  onSelect: (city: City) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (cities.length <= 1) return null;

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-pill',
          'bg-[var(--surface-tertiary)] hover:bg-[var(--surface-hover)]',
          'text-xs font-semibold text-[var(--text-secondary)]',
          'transition-all duration-200',
          'border border-transparent hover:border-[var(--border-default)]'
        )}
      >
        <NavigationArrow className="h-3.5 w-3.5 text-[var(--primary)]" weight="fill" />
        <span>{selectedCity?.name || 'Город'}</span>
        <CaretDown
          className={cn(
            'h-3 w-3 text-[var(--text-tertiary)] transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
          weight="bold"
        />
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute top-full right-0 mt-2 z-[60]',
            'bg-[var(--surface-primary)] backdrop-blur-xl',
            'border border-[var(--border-default)]',
            'rounded-card shadow-float overflow-hidden min-w-[160px]',
            'animate-fade-in'
          )}
        >
          {cities.map((city) => (
            <button
              key={city.id}
              onClick={() => {
                onSelect(city);
                setIsOpen(false);
              }}
              className={cn(
                'w-full px-4 py-2.5 text-left text-sm font-medium',
                'hover:bg-[var(--surface-secondary)] transition-colors duration-150',
                selectedCity?.id === city.id && 'bg-[var(--primary-subtle)] text-[var(--primary)]'
              )}
            >
              {city.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


/* ============================
   Delivery Info
   ============================ */
function DeliveryInfo() {
  const selectedAddress = useAddressStore(selectSelectedAddress);
  if (!selectedAddress) return null;

  const isInZone = selectedAddress.isInZone;

  if (!isInZone) {
    return (
      <div className="flex items-start gap-3 p-4 rounded-card bg-[var(--error)]/10">
        <div className="w-8 h-8 rounded-pill bg-[var(--error)]/20 flex items-center justify-center flex-shrink-0">
          <WarningCircle className="h-5 w-5 text-[var(--error)]" weight="fill" />
        </div>
        <div>
          <p className="font-bold text-sm text-[var(--error)]">Вне зоны доставки</p>
          <p className="text-xs text-[var(--text-secondary)] mt-0.5">
            К сожалению, мы пока не доставляем по этому адресу
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-card bg-[var(--primary-subtle)] p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 rounded-pill bg-[var(--primary)] flex items-center justify-center">
          <CheckCircle className="h-4 w-4 text-white" weight="fill" />
        </div>
        <span className="font-bold text-sm text-[var(--text-primary)]">Доставка доступна</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <InfoCard
          icon={<Coins className="h-4 w-4" weight="fill" />}
          label="Доставка"
          value={selectedAddress.deliveryCost === 0 ? 'Бесплатно' : `${selectedAddress.deliveryCost} ₽`}
        />
        <InfoCard
          icon={<Clock className="h-4 w-4" weight="fill" />}
          label="Время"
          value={selectedAddress.estimatedTime || '~60 мин'}
        />
        <InfoCard
          icon={<ShoppingCart className="h-4 w-4" weight="fill" />}
          label="Минимум"
          value={`${selectedAddress.minOrderSum || 0} ₽`}
        />
      </div>
    </div>
  );
}

/* ============================
   Не смогли проверить адрес
   ============================ */
// Отдельный блок вместо красного «вне зоны»: сбой геокодера бэк отдаёт с
// HTTP 200, и житель зоны получал отказ в доставке вместо предложения повторить.
function AddressCheckFailed({ onRetry, isChecking }: { onRetry: () => void; isChecking: boolean }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-card bg-[var(--warning)]/10 border border-[var(--warning)]/20">
      <div className="w-8 h-8 rounded-pill bg-[var(--warning)]/20 flex items-center justify-center flex-shrink-0">
        <WarningCircle className="h-5 w-5 text-[var(--warning)]" weight="fill" />
      </div>
      <div className="min-w-0">
        <p className="font-bold text-sm text-[var(--warning)]">Не удалось проверить адрес</p>
        <p className="text-xs text-[var(--text-secondary)] mt-0.5">
          Это сбой проверки, а не отказ в доставке. Повторите попытку или укажите дом на карте.
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-2.5">
          <button
            type="button"
            onClick={onRetry}
            disabled={isChecking}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-pill',
              'bg-[var(--surface-primary)] border border-[var(--border-default)]',
              'text-xs font-semibold text-[var(--text-primary)]',
              'transition-colors duration-150 disabled:opacity-60'
            )}
          >
            <ArrowClockwise className={cn('h-4 w-4', isChecking && 'animate-spin')} weight="bold" />
            <span>{isChecking ? 'Проверяем…' : 'Повторить'}</span>
          </button>
          <a
            href="tel:+78633335050"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-pill text-xs font-semibold text-[var(--primary)]"
          >
            <Phone className="h-4 w-4" weight="fill" />
            <span>+7 (863) 333-50-50</span>
          </a>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex flex-col items-center p-2.5 rounded-card bg-[var(--surface-primary)]/80 backdrop-blur-sm">
      <div className="text-[var(--primary)] mb-1">{icon}</div>
      <span className="t-nano text-[var(--text-tertiary)] uppercase tracking-wide">{label}</span>
      <span className="font-bold text-xs text-[var(--text-primary)] mt-0.5">{value}</span>
    </div>
  );
}


/* ============================
   Fullscreen Content
   ============================ */
function FullscreenContent({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  const {
    isSheetOpen,
    selectedCity,
    selectedAddress,
    cities,
    deliveryPoints,
    isLoading,
    addressCheckError,
    fetchCities,
    fetchDeliveryPoints,
    setSelectedCity,
    checkAddress,
    setSelectedAddress,
    clearAddressCheckError,
  } = useAddressStore();

  const [mapAddress, setMapAddressRaw] = useState(selectedAddress?.address || '');
  const [pickingOnMap, setPickingOnMap] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [addressWarning, setAddressWarning] = useState<string | null>(null);
  const mapHandleRef = useRef<YandexMapHandle>(null);
  
  // Remember the address state when the sheet opened, so we can restore on cancel
  const initialAddressRef = useRef(selectedAddress);

  // Wrap setMapAddress to clear selectedAddress and warning when user empties the field
  const setMapAddress = useCallback((value: string) => {
    setMapAddressRaw(value);
    setAddressWarning(null);
    clearAddressCheckError();
    if (value.trim().length === 0) {
      setSelectedAddress(null);
    }
  }, [setSelectedAddress, clearAddressCheckError]);

  // Компонент не размонтируется при закрытии (карту держим в DOM), поэтому
  // снимок «как было» и текст в поле обновляем при КАЖДОМ открытии — иначе
  // они остаются от первого открытия и расходятся с подтверждённым адресом.
  useEffect(() => {
    if (!isSheetOpen) return;
    const current = useAddressStore.getState().selectedAddress;
    initialAddressRef.current = current;
    setMapAddressRaw(current?.address || '');
    setAddressWarning(null);
    clearAddressCheckError();
  }, [isSheetOpen, clearAddressCheckError]);

  // Fetch cities on mount
  useEffect(() => {
    if (cities.length === 0) fetchCities();
  }, [cities.length, fetchCities]);

  // Ensure delivery points are loaded
  useEffect(() => {
    if (selectedCity && deliveryPoints.length === 0) fetchDeliveryPoints(selectedCity.id);
  }, [selectedCity, deliveryPoints.length, fetchDeliveryPoints]);

  const handleConfirm = useCallback(async () => {
    const typed = mapAddress.trim();
    let current = useAddressStore.getState().selectedAddress;
    if (!current) return;

    // Зона могла считаться для ДРУГОГО текста: если проверка адреса упала,
    // в сторе остаётся прошлый адрес. Склеивать нельзя — заказ уедет в чужую
    // точку по чужой цене. Перепроверяем и подтверждаем уже по результату.
    if (typed && current.address.trim() !== typed) {
      const result = await checkAddress(typed);
      if (!result.isInZone) return;
      current = useAddressStore.getState().selectedAddress;
    }

    if (!current?.isInZone) return;

    ymAddressSelected();
    tmrAddressSelected();
    onConfirm();
  }, [mapAddress, onConfirm, checkAddress]);

  // Повтор проверки после сбоя (кнопка «Повторить» в блоке ошибки)
  const handleRetryCheck = useCallback(() => {
    const typed = mapAddress.trim();
    if (typed) checkAddress(typed);
  }, [mapAddress, checkAddress]);

  // When closing without confirming, restore the previous valid address
  const handleCancel = useCallback(() => {
    const current = useAddressStore.getState().selectedAddress;
    if (!current?.isInZone) {
      if (initialAddressRef.current?.isInZone) {
        // Прошлый подтверждённый адрес важнее незавершённой попытки.
        setSelectedAddress(initialAddressRef.current);
      } else if (!current && initialAddressRef.current) {
        setSelectedAddress(initialAddressRef.current);
      }
      // Адрес вне зоны НЕ стираем: гейт корзины требует непустой адрес, и
      // обнуление запирало человека в цикле «корзина → карта → корзина».
      // Про отсутствие доставки он узнаёт на шаге «Данные доставки».
    }
    onCancel();
  }, [onCancel, setSelectedAddress]);

  // Click on the transparent overlay div → convert pixel to geo → DaData reverse geocode
  // Only trigger on clean clicks (no drag). Track mousedown position to distinguish.
  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);

  const handlePickPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    pointerDownPos.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handlePickPointerUp = useCallback(async (e: React.PointerEvent<HTMLDivElement>) => {
    if (!pickingOnMap || isGeocoding) return;

    // Check if it was a drag (moved more than 5px)
    const down = pointerDownPos.current;
    pointerDownPos.current = null;
    if (!down) return;
    const dx = e.clientX - down.x;
    const dy = e.clientY - down.y;
    if (Math.sqrt(dx * dx + dy * dy) > 5) return; // was a drag, ignore

    const mapHandle = mapHandleRef.current;
    if (!mapHandle) return;

    const geo = mapHandle.pixelToGeo(e.clientX, e.clientY);
    if (!geo) return;

    setIsGeocoding(true);
    setAddressWarning(null);
    // Блок сбоя проверки перекрывает предупреждение о доме: если его не снять,
    // после тапа по карте человек увидит старую ошибку вместо нового ответа.
    clearAddressCheckError();
    mapHandle.placeMarker(geo.lat, geo.lon);

    try {
      const result = await deliveryApi.reverseGeocode(geo.lat, geo.lon);
      const address = result.address || '';
      const data = result.data;

      // Validate that address has at least street and house number
      if (!address || !data?.house) {
        setAddressWarning('Укажите точный адрес с номером дома');
        setMapAddressRaw(address);
        setPickingOnMap(false);
        // Don't set as selected address — it's incomplete
        setSelectedAddress(null);
        return;
      }

      setMapAddressRaw(address);
      setPickingOnMap(false);

      setSelectedAddress({
        address,
        coordinates: { lat: geo.lat, lon: geo.lon },
        isInZone: false,
      });

      if (selectedCity) {
        await checkAddress(address);
        const current = useAddressStore.getState().selectedAddress;
        if (current) {
          setSelectedAddress({ ...current, coordinates: { lat: geo.lat, lon: geo.lon } });
        }
      }
    } catch (error) {
      console.error('Map pick failed:', error);
      // Молчать нельзя: метка стоит, режим выбора включён, адрес не подставился —
      // человек тычет в дом снова и снова без единого ответа.
      setAddressWarning('Не удалось определить адрес по этой точке');
      setPickingOnMap(false);
      import('@/lib/source-tracker').then(({ captureEvent }) => {
        captureEvent('address_check_failed', {
          reason: 'reverse_geocode_failed',
          http_status: null,
          query: `${geo.lat},${geo.lon}`,
          has_prev_address: !!useAddressStore.getState().selectedAddress,
        });
      }).catch(() => { /* ignore */ });
    } finally {
      setIsGeocoding(false);
    }
  }, [pickingOnMap, isGeocoding, selectedCity, checkAddress, setSelectedAddress, clearAddressCheckError]);

  const typedAddress = mapAddress.trim();
  // Инфо о доставке показываем только для того адреса, для которого зону
  // реально считали: иначе человек видит цену и минималку от прошлого адреса.
  // Сравниваем по trim — геокодер может вернуть строку с лишними пробелами,
  // и точное сравнение заперло бы кнопку на валидном адресе.
  const hasAddress = typedAddress.length > 0 && selectedAddress?.address.trim() === typedAddress;
  // Подтверждать можно только проверенный текст — иначе к новому адресу
  // приклеится зона и цена прошлого.
  const canConfirm = hasAddress && selectedAddress?.isInZone === true;

  return (
    <div
      className={cn(
        'address-sheet-content',
        // absolute, а не fixed: на десктопе родитель — окно, и содержимое
        // обязано жить внутри него. На мобильном родитель по-прежнему на
        // весь экран, поэтому визуально ничего не меняется.
        'absolute inset-0 overflow-hidden',
      )}
    >
      {/* ===== MAP (full screen) ===== */}
      <div
        className="absolute inset-0 z-0"
        style={pickingOnMap ? { cursor: 'crosshair' } : undefined}
        onPointerDown={pickingOnMap ? handlePickPointerDown : undefined}
        onPointerUp={pickingOnMap ? handlePickPointerUp : undefined}
      >
        <YandexMap
          ref={mapHandleRef}
          coordinates={selectedAddress?.coordinates}
          zoom={10}
          showZones
          className="!rounded-none !min-h-0"
        />
      </div>

      {/* ===== FLOATING UI ===== */}

      {/* --- Top: search bar + close button in one row --- */}
      <div className="absolute top-3 left-3 right-3 z-10 flex items-center gap-2 pointer-events-auto md:top-4 md:left-4 md:right-4">
        {/* Search input (takes remaining space) */}
        <div className={cn(
          'flex-1 min-w-0',
          'bg-[var(--surface-primary)] rounded-card shadow-float',
          'border border-[var(--border-default)]',
          'p-1.5'
        )}>
          <AddressInput autoFocus={!pickingOnMap} value={mapAddress} onChange={setMapAddress} onSelect={(s) => {
            if (!s.data.house) {
              setAddressWarning('Укажите точный адрес с номером дома');
            }
          }} />
        </div>

        {/* Close button */}
        <button
          onClick={handleCancel}
          className={cn(
            'flex-shrink-0 w-12 h-12 rounded-pill flex items-center justify-center',
            'bg-[var(--surface-primary)] shadow-float',
            'border border-[var(--border-default)]',
            'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
            'transition-all duration-200'
          )}
        >
          <X className="h-5 w-5" weight="bold" />
        </button>
      </div>

      {/* --- Right side: pick button (vertically centered) --- */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10 pointer-events-auto md:right-4">
        <button
          type="button"
          onClick={() => setPickingOnMap(!pickingOnMap)}
          className={cn(
            'w-12 h-12 rounded-pill flex items-center justify-center',
            'transition-all duration-200',
            // Включённое состояние — то самое место, где тени не было ВООБЩЕ.
            // Здесь стоял одинокий класс ЦВЕТА тени через переменную, без самой
            // тени рядом: Tailwind 4 кладёт такую запись в значение box-shadow,
            // значение получается невалидным и тень схлопывается в ничто.
            // Судя по коду, автор хотел фирменный ореол, поэтому включённое
            // состояние переведено на роль «свечение» (ответ 35 = Б), а
            // спокойное — на роль «парение»: кнопка висит над картой и должна
            // от неё отрываться в любом состоянии.
            // Роли специально разведены по веткам, а не вынесены в общую
            // строку: tailwind-merge 2.6.1 про них не знает, оставил бы оба
            // класса на элементе, и победил бы порядок в собранном CSS,
            // а не порядок в этой строке.
            pickingOnMap
              ? 'bg-[var(--primary)] text-white shadow-glow'
              : 'bg-[var(--surface-primary)] text-[var(--text-secondary)] border border-[var(--border-default)] hover:text-[var(--text-primary)] shadow-float'
          )}
        >
          <Crosshair className="h-5 w-5" weight={pickingOnMap ? 'fill' : 'bold'} />
        </button>
      </div>

      {/* --- Below search: city selector --- */}
      <div className="absolute top-[76px] left-3 z-10 pointer-events-auto md:top-[84px] md:left-4">
        <CitySelector cities={cities} selectedCity={selectedCity} onSelect={setSelectedCity} />
      </div>

      {/* --- Bottom panel (floating card) --- */}
      <div className={cn(
        'absolute bottom-0 left-0 right-0 z-10 pointer-events-auto',
        'bg-[var(--surface-primary)] backdrop-blur-md',
        'border-t border-[var(--border-default)]',
        'rounded-t-screen md:rounded-t-card',
        // Тень намеренно оставлена произвольной: она направлена ВВЕРХ, а все
        // системные роли глубины светят вниз (0 1px / 0 4px / 0 8px). Замена
        // на shadow-float убрала бы отрыв панели от карты. Нужна роль
        // «шторка снизу» в токенах — вынесено в отчёт.
        'shadow-[0_-4px_24px_rgba(0,0,0,0.1)]',
        'transition-all duration-300 ease-out'
      )}>
        <div className="px-4 pt-4 pb-2 md:px-6 md:pt-5 md:pb-3 space-y-3 md:space-y-4">
          {/* Picking hint */}
          {pickingOnMap && (
            <div className={cn(
              'flex items-center justify-center gap-2.5 px-4 py-2.5 rounded-card',
              'bg-[var(--primary-subtle)] border border-[var(--primary)]/20',
              'animate-fade-in'
            )}>
              {isGeocoding ? (
                <div className="h-4 w-4 border-2 border-[var(--primary)] border-t-transparent rounded-pill animate-spin" />
              ) : (
                <Crosshair className="h-4 w-4 text-[var(--primary)]" weight="fill" />
              )}
              <span className="text-sm font-semibold text-[var(--primary)]">
                {isGeocoding ? 'Определяем адрес...' : 'Нажмите на нужный дом'}
              </span>
            </div>
          )}
          {/* Приоритет блоков: не смогли проверить → предупреждение → инфо о доставке */}
          {addressCheckError ? (
            <AddressCheckFailed onRetry={handleRetryCheck} isChecking={isLoading} />
          ) : addressWarning ? (
            <div className="flex items-start gap-3 p-4 rounded-card bg-[var(--warning)]/10 border border-[var(--warning)]/20">
              <WarningCircle className="h-5 w-5 text-[var(--warning)] flex-shrink-0 mt-0.5" weight="fill" />
              <div>
                <p className="font-bold text-sm text-[var(--warning)]">{addressWarning}</p>
                <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                  Нажмите точнее на нужный дом на карте или уточните адрес в строке поиска
                </p>
              </div>
            </div>
          ) : hasAddress ? (
            <DeliveryInfo />
          ) : null}
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            size="lg"
            // Скругление и тень приходят из примитива Button (size=lg даёт
            // rounded-card, variant=default даёт shadow-raised). Локальные
            // rounded-xl / md:rounded-2xl / shadow-lg отсюда УБРАНЫ: cn() собран
            // на tailwind-merge 2.6.1, который про rounded-card не знает и не
            // схлопывает пару — на элемент попадали ОБА класса, а побеждал не
            // тот, что правее в строке, а тот, что ниже в собранном CSS.
            // rounded-xl лежит ниже rounded-card, поэтому кнопка оставалась на
            // 16px, когда весь остальной сайт уже перешёл на 20. shadow-lg по
            // той же причине не рисовался вовсе — его перекрывал shadow-raised
            // примитива. Здесь остаются только размеры, скругление системное.
            className={cn('w-full h-12 md:h-14')}
          >
            {canConfirm ? (
              <>
                <CheckCircle className="h-5 w-5" weight="fill" />
                <span>Подтвердить адрес</span>
              </>
            ) : (
              'Выберите адрес'
            )}
          </Button>
        </div>
        <div className="h-[env(safe-area-inset-bottom)]" />
      </div>
    </div>
  );
}


/* ============================
   Main Export
   ============================ */
export function AddressSheet() {
  const { isSheetOpen, closeSheet, confirmSheet } = useAddressStore();
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  // Track if the sheet has ever been opened so we keep the map in DOM
  const [everOpened, setEverOpened] = useState(false);

  useEffect(() => {
    if (isSheetOpen) {
      setEverOpened(true);
      setVisible(true);
      setClosing(false);
    } else if (visible) {
      // Start close animation
      setClosing(true);
    }
  }, [isSheetOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAnimationEnd = useCallback(() => {
    if (closing) {
      setVisible(false);
      setClosing(false);
    }
  }, [closing]);

  const handleClose = useCallback(() => {
    closeSheet();
  }, [closeSheet]);

  // Подтверждение отделено от закрытия: только оно возвращает человека
  // в корзину, если адрес спрашивали по дороге туда.
  const handleConfirmed = useCallback(() => {
    confirmSheet();
  }, [confirmSheet]);

  // Don't render anything until the sheet has been opened at least once
  if (!everOpened) return null;

  // After first open, keep the DOM alive but hide it when not visible
  const isHidden = !visible && !closing;

  return (
    <div
      className="fixed inset-0 z-[9499]"
      role="dialog"
      aria-modal="true"
      aria-label="Выбор адреса доставки"
      style={isHidden ? { display: 'none' } : undefined}
    >
      {/* Overlay */}
      <div
        className={cn(
          'absolute inset-0 bg-black/60 backdrop-blur-sm',
          closing ? 'address-sheet-overlay-out' : 'address-sheet-overlay-in'
        )}
        onAnimationEnd={handleAnimationEnd}
      />
      {/* Content */}
      <div
        className={cn(
          'absolute inset-0 z-[1]',
          // Десктоп: окно по центру, а не карта на весь монитор. Ширина и
          // высота идут от вьюпорта с потолком, поэтому окно остаётся
          // соразмерным и на 13", и на 27".
          'lg:inset-auto lg:left-1/2 lg:top-1/2',
          'lg:w-[min(1060px,90vw)] lg:h-[min(700px,84vh)]',
          'lg:rounded-screen lg:overflow-hidden',
          'lg:border lg:border-[var(--border-default)]',
          // Окно карты на десктопе — модалка, объём ей оставлен (ответ 35 = 2).
          'lg:shadow-deep',
          closing ? 'address-sheet-out' : 'address-sheet-in'
        )}
      >
        <FullscreenContent onConfirm={handleConfirmed} onCancel={handleClose} />
      </div>
    </div>
  );
}
